"use client";

import { Collapsible as CollapsiblePrimitive } from "@base-ui/react/collapsible";
import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import type { SpacingToken } from "./box-classes.ts";

/** The panel clips only while settling; at rest shadows and focus rings may overflow it. */
const CollapsibleSettlingContext = createContext<{
  settling: boolean;
  setSettling: (settling: boolean) => void;
}>({ setSettling: () => undefined, settling: false });

export function Collapsible({
  open,
  onOpenChange,
  ...props
}: CollapsiblePrimitive.Root.Props): React.ReactElement {
  const [settling, setSettling] = useState(false);
  // A controlled `open` change bypasses `onOpenChange`; read before paint so the first frame is clipped.
  const previousOpen = useRef(open);
  useLayoutEffect(() => {
    if (previousOpen.current === open) return;
    previousOpen.current = open;
    setSettling(true);
  }, [open]);
  const context = React.useMemo(() => ({ setSettling, settling }), [settling]);
  return (
    <CollapsibleSettlingContext.Provider value={context}>
      <CollapsiblePrimitive.Root
        data-slot="collapsible"
        onOpenChange={(next, details) => {
          setSettling(true);
          onOpenChange?.(next, details);
        }}
        open={open}
        {...props}
      />
    </CollapsibleSettlingContext.Provider>
  );
}

export function CollapsibleTrigger({
  className,
  ...props
}: CollapsiblePrimitive.Trigger.Props): React.ReactElement {
  return (
    <CollapsiblePrimitive.Trigger
      className={className}
      data-slot="collapsible-trigger"
      {...props}
    />
  );
}

const SPACING_STEPS: Record<SpacingToken, number> = {
  none: 0,
  "2xs": 0.5,
  xs: 1,
  sm: 2,
  base: 3,
  md: 4,
  lg: 6,
  xl: 8,
  "2xl": 12,
};

function spacingVar(token: SpacingToken | undefined): string {
  const steps = token ? SPACING_STEPS[token] : 0;
  return steps === 0 ? "0px" : `calc(var(--spacing) * ${steps})`;
}

export type CollapsiblePanelProps = CollapsiblePrimitive.Panel.Props & {
  /** The `gap` of the parent flex stack, folded so the sibling below does not step when the panel unmounts. */
  stackGap?: SpacingToken;
  /** Clip-box room around the content (padding offset by negative margin) so shadows are not cut while settling. */
  bleed?: SpacingToken;
};

/** The distances travel as custom properties so the class list stays one constant string. */
export function CollapsiblePanel({
  className,
  stackGap,
  bleed,
  style,
  onTransitionEnd,
  onTransitionCancel,
  ref,
  ...props
}: CollapsiblePanelProps): React.ReactElement {
  const { settling, setSettling } = useContext(CollapsibleSettlingContext);
  const panel = useRef<HTMLDivElement | null>(null);
  // Only the panel's own transition ends the settle, not a child's.
  const settle = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) setSettling(false);
  };
  useEffect(() => {
    if (!settling) return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        if ((panel.current?.getAnimations?.().length ?? 0) === 0) setSettling(false);
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [settling, setSettling]);
  return (
    <CollapsiblePrimitive.Panel
      className={cn(
        "h-(--collapsible-panel-height) p-(--collapsible-bleed) -m-(--collapsible-bleed) transition-[height,opacity,margin] duration-slow ease-spring data-ending-style:h-0 data-starting-style:h-0 data-ending-style:opacity-0 data-starting-style:opacity-0",
        "data-settling:overflow-hidden data-starting-style:overflow-hidden data-ending-style:overflow-hidden",
        "[:not(:last-child)>&]:data-ending-style:-mb-[calc(var(--collapsible-bleed)_+_var(--collapsible-stack-gap))] [:not(:last-child)>&]:data-starting-style:-mb-[calc(var(--collapsible-bleed)_+_var(--collapsible-stack-gap))] [:last-child:not(:only-child)>&]:data-ending-style:-mt-[calc(var(--collapsible-bleed)_+_var(--collapsible-stack-gap))] [:last-child:not(:only-child)>&]:data-starting-style:-mt-[calc(var(--collapsible-bleed)_+_var(--collapsible-stack-gap))]",
        "motion-reduce:transition-opacity motion-reduce:duration-fast",
        className,
      )}
      data-settling={settling ? "" : undefined}
      data-slot="collapsible-panel"
      onTransitionCancel={(event) => {
        settle(event);
        onTransitionCancel?.(event);
      }}
      onTransitionEnd={(event) => {
        settle(event);
        onTransitionEnd?.(event);
      }}
      ref={(node: HTMLDivElement | null) => {
        panel.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      style={(state) =>
        ({
          ...(typeof style === "function" ? style(state) : style),
          "--collapsible-bleed": spacingVar(bleed),
          "--collapsible-stack-gap": spacingVar(stackGap),
        }) as React.CSSProperties
      }
      {...props}
    />
  );
}

export {
  CollapsiblePanel as CollapsibleContent,
};
